import React from "react";
import { PaymentDetail } from "@features/cx/payments/types";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import {
  usePaymentMode,
  useCheckRefundPayment,
  useReviewRefundPayment,
  usePaymentChoices,
} from "../../../../hooks";
import { AlertTriangle, ChevronLeft, Check } from "lucide-react";
import { Button } from "@shared/ui/button";

interface ReviewStepProps {
  payment: PaymentDetail;
  refundData: any;
  onConfirm: (onNext?: () => void, refundId?: string) => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  payment,
  refundData,
  onConfirm,
  onPrevious,
  onNext,
}) => {
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();

  const { data: paymentModesData } = usePaymentMode(Number(remoteUtilityId));
  const { data: refundTypeChoicesData } = usePaymentChoices("refund_types");
  const { data: dispositionsData } = usePaymentChoices("dispositions");
  const { data: refundReasonsData } = usePaymentChoices("refund_reasons");

  const paymentModeList = Array.isArray(paymentModesData)
    ? paymentModesData
    : Array.isArray(paymentModesData?.result)
      ? paymentModesData.result
      : [];

  const selectedMode = paymentModeList.find(
    (m: any) => m.name === refundData?.refundMethod,
  );
  const customFields = selectedMode?.customFields || [];

  const { mutate: reviewRefund, isPending } = useReviewRefundPayment();

  const handleConfirm = () => {
    const customFieldsPayload: Record<string, any> = {};
    customFields.forEach((cf: any) => {
      const fieldName = cf.key || cf.id?.toString();
      if (refundData[fieldName] !== undefined) {
        const val = refundData[fieldName];
        customFieldsPayload[fieldName] =
          typeof val === "object" && val?.startDate ? val.startDate : val;
      }
    });

    const refundTypeKey =
      refundTypeChoicesData?.result?.find(
        (r: any) => r.value === refundData.refundType,
      )?.key ?? 0;
    const dispositionKey =
      dispositionsData?.result?.find(
        (d: any) => d.value === refundData.disposition,
      )?.key ?? 0;

    const payload = {
      refund_type: refundTypeKey,
      disposition: dispositionKey,
      refund_amount: Number(
        refundData.finalAmount || refundData.refundAmount || 0,
      ).toFixed(2),
      refund_reason: refundData.refundReason,
      refund_method:
        refundData.disposition === "Create Credit Note"
          ? ""
          : refundData.refundMethod || "",
      approver: refundData.approver || "",
      remarks: refundData.remarks || "",
      ...customFieldsPayload,
    };

    reviewRefund(
      { paymentId: payment.id, payload },
      {
        onSuccess: (data: any) => {
          onConfirm(onNext, data?.result?.refundId);
        },
      },
    );
  };

  const isPartial = refundData?.refundType === "Partial Refund";
  const remainingBalance =
    refundData?.refundableAmount - refundData?.finalAmount;

  const refundReasonLabel =
    refundReasonsData?.result?.find(
      (r: any) => String(r.key) === String(refundData?.refundReason),
    )?.value ||
    refundData?.refundReason ||
    "—";

  return (
    <div className="space-y-6">
      {isPartial && remainingBalance > 0 && (
        <div className="bg-[#fffde7] border border-[#fbc02d] rounded-lg p-4 flex gap-3 text-sm text-[#8d6e63] items-start">
          <AlertTriangle className="w-5 h-5 text-[#fbc02d] shrink-0 mt-0.5" />
          <p>
            Partial refund: the original receipt will be marked Partially
            Refunded for{" "}
            <span className="font-bold">
              {currency} {Number(refundData?.finalAmount || 0).toFixed(2)}
            </span>
            , with{" "}
            <span className="font-bold">
              {currency} {remainingBalance.toFixed(2)}
            </span>{" "}
            still available to refund later.
          </p>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg p-6 bg-slate-50/50">
        <div className="flex flex-col">
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Consumer"
            value={`${payment.consumerFirstName} ${payment.consumerLastName} (${payment.consumerNo || payment.consumerId})`}
          />
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Original Receipt"
            value={payment.receiptNo || "N/A"}
          />
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Original Amount"
            value={`${currency} ${Number(payment.amount || 0).toFixed(2)}`}
          />
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Refund Type"
            value={refundData?.refundType}
          />
          {refundData?.disposition && (
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Disposition"
              value={refundData?.disposition}
            />
          )}
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Refund Reason"
            value={refundReasonLabel}
          />
          {refundData?.disposition === "Refund to Customer" && (
            <>
              <ValueLabel
                layout="horizontal"
                className="w-full justify-between py-3 border-b border-dashed border-slate-200"
                label="Refund Method"
                value={refundData?.refundMethod || "—"}
              />
              {customFields.map((cf: any) => {
                const fieldName = cf.key || cf.id?.toString();
                const value = refundData?.[fieldName];
                if (!value) return null;

                return (
                  <ValueLabel
                    key={fieldName}
                    layout="horizontal"
                    className="w-full justify-between py-3 border-b border-dashed border-slate-200"
                    label={cf.label}
                    value={
                      value instanceof Date
                        ? value.toLocaleDateString()
                        : typeof value === "object" && value?.startDate
                          ? new Date(value.startDate).toLocaleDateString()
                          : String(value)
                    }
                  />
                );
              })}
            </>
          )}
          <ValueLabel
            layout="horizontal"
            className="w-full justify-between py-3 border-b border-dashed border-slate-200"
            label="Approver"
            value={refundData?.approver || "—"}
          />
          {refundData?.remarks && (
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Remarks"
              value={refundData?.remarks}
            />
          )}
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex justify-between items-center">
        <span className="font-bold text-red-600 text-sm">Total to Refund</span>
        <span className="text-2xl font-black text-red-600">
          {currency} {refundData?.finalAmount?.toFixed(2)}
        </span>
      </div>

      <div className="flex justify-end items-center pt-2 gap-2">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>
        <Button variant="default" onClick={handleConfirm} disabled={isPending}>
          <Check className="w-4 h-4" />
          {isPending ? "Processing..." : "Confirm & Process Refund"}
        </Button>
      </div>
    </div>
  );
};
